import { useEffect, useState } from "react";
import api, { extractErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { MessageSquare, Users, BookOpen, TrendingUp, ArrowUpRight, Inbox, Sparkles, Clock, Zap } from "lucide-react";
import { toast } from "sonner";

const STAGE_COLORS = {
  New: "bg-slate-100 text-slate-700 border-slate-300",
  Contact: "bg-blue-50 text-blue-800 border-blue-200",
  Consider: "bg-amber-50 text-amber-800 border-amber-200",
  Register: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Lost: "bg-red-50 text-red-800 border-red-200",
};

const STAGE_LABELS = {
  New: "全新",
  Contact: "沟通中",
  Consider: "考虑报名",
  Register: "成功报名",
  Lost: "没回复",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [convs, setConvs] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadAll = () => {
    api.get("/dashboard/stats").then(r => setStats(r.data));
    api.get("/conversations").then(r => setConvs(r.data.slice(0, 5)));
    api.get("/follow-ups").then(r => setFollowUps(r.data));
    api.get("/settings").then(r => setSettings(r.data));
  };
  useEffect(() => { loadAll(); }, []);

  const toggleAuto = async () => {
    try {
      const { data } = await api.patch("/settings", { auto_reply_enabled: !settings?.auto_reply_enabled });
      setSettings(data);
      toast.success(data.auto_reply_enabled ? "Auto AI reply ON" : "Auto AI reply OFF");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const sendAiFollowUp = async (convId) => {
    setBusy(convId);
    try {
      await api.post(`/conversations/${convId}/ai-follow-up`);
      toast.success("Follow-up sent");
      loadAll();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(null); }
  };

  const kpis = [
    { label: "Open Conversations", value: stats?.open_conversations ?? "—", icon: MessageSquare, tid: "kpi-conversations", accent: "text-emerald-700" },
    { label: "Unread Messages", value: stats?.unread_messages ?? "—", icon: Inbox, tid: "kpi-unread", accent: "text-orange-600" },
    { label: "Total Leads", value: stats?.total_leads ?? "—", icon: Users, tid: "kpi-leads", accent: "text-slate-900" },
    { label: "Deals Won", value: stats?.won_deals ?? "—", icon: TrendingUp, tid: "kpi-won", accent: "text-emerald-700" },
  ];

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      {/* Header */}
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-700 font-semibold">Dashboard</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Good day, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-slate-500 mt-1.5 text-sm">育伯乐正在与您一起赋能教育生态圈。以下是今日的跟进与服务概况。</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="toggle-auto-reply"
            onClick={toggleAuto}
            className={`inline-flex items-center gap-2 px-4 py-2.5 border-2 text-sm font-semibold rounded-sm transition-all ${
              settings?.auto_reply_enabled
                ? "bg-orange-50 border-orange-500 text-orange-700 shadow-brutal-accent"
                : "bg-white border-slate-300 text-slate-600 hover:border-orange-400"
            }`}
            title="Auto-reply on every new inbound WhatsApp message"
          >
            <Zap size={15} className={settings?.auto_reply_enabled ? "text-orange-600" : ""} />
            Auto AI reply: {settings?.auto_reply_enabled ? "ON" : "OFF"}
          </button>
          <Link
            to="/inbox"
            data-testid="dashboard-open-inbox"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 bg-slate-950 text-white text-sm font-semibold rounded-sm hover:bg-emerald-700 transition-colors"
          >
            Open Inbox <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {kpis.map(({ label, value, icon: Icon, tid, accent }) => (
          <div key={label} data-testid={tid} className="bg-white border border-slate-200 p-5 rounded-sm hover:-translate-y-[1px] hover:shadow-sm transition-transform duration-200">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
              <Icon size={16} className={accent} strokeWidth={2} />
            </div>
            <div className={`font-display text-4xl font-black tracking-tight ${accent}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Two-column: Pipeline + Recent conversations */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-sm">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Pipeline</div>
              <div className="font-display text-xl font-bold mt-0.5">Deal stages</div>
            </div>
            <Link to="/leads" className="text-xs font-semibold text-emerald-700 hover:underline" data-testid="dashboard-view-leads">View leads →</Link>
          </div>
          <div className="p-5 space-y-3">
            {stats && Object.entries(stats.pipeline).map(([stage, count]) => {
              const total = stats.total_leads || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={stage}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block text-[11px] font-bold tracking-wider px-2 py-0.5 border ${STAGE_COLORS[stage]}`}>{STAGE_LABELS[stage] || stage}</span>
                      <span className="text-xs text-slate-500">{count} leads</span>
                    </div>
                    <span className="text-xs font-mono text-slate-500">{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-700 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm">
          <div className="p-5 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Recent</div>
            <div className="font-display text-xl font-bold mt-0.5">Latest conversations</div>
          </div>
          <div className="divide-y divide-slate-100">
            {convs.length === 0 && (
              <div className="p-6 text-sm text-slate-500">No conversations yet. Try the "Simulate Inbound" button in Inbox.</div>
            )}
            {convs.map(c => (
              <Link
                key={c.id}
                to={`/inbox?c=${c.id}`}
                data-testid={`dashboard-conv-${c.id}`}
                className="block p-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-sm truncate">{c.lead_name}</div>
                  {c.unread_count > 0 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 bg-orange-500 text-white rounded-sm">{c.unread_count}</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{c.last_message || "—"}</div>
                <div className="text-[10px] text-slate-400 mt-1">{c.phone}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Follow-up nudges */}
      {followUps.length > 0 && (
        <div className="mt-6 bg-white border-2 border-orange-300 rounded-sm shadow-brutal-accent" data-testid="followups-section">
          <div className="p-5 border-b border-orange-200 bg-orange-50 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Clock size={18} className="text-orange-600" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-orange-700 font-bold">Needs a nudge</div>
                <div className="font-display text-lg font-bold text-orange-900">{followUps.length} lead{followUps.length > 1 ? "s" : ""} unresponded &gt; 24h</div>
              </div>
            </div>
            <div className="text-xs text-orange-700">Click "AI follow-up" to send a warm nudge</div>
          </div>
          <div className="divide-y divide-slate-100">
            {followUps.slice(0, 6).map(c => (
              <div key={c.id} className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {(c.lead_name || "?").split(" ").slice(0,2).map(s => s[0]?.toUpperCase()).join("")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{c.lead_name}</div>
                  <div className="text-xs text-slate-500 truncate">Last: "{c.last_message}"</div>
                </div>
                <Link to={`/inbox?c=${c.id}`} className="text-xs font-semibold text-slate-600 hover:underline">Open chat</Link>
                <button
                  data-testid={`ai-followup-${c.id}`}
                  onClick={() => sendAiFollowUp(c.id)}
                  disabled={busy === c.id}
                  className="px-3 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-sm hover:bg-orange-600 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Sparkles size={12} /> {busy === c.id ? "Sending…" : "AI follow-up"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Course knowledge base card */}
      <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 bg-slate-100 flex items-center justify-center rounded-sm">
            <BookOpen size={20} className="text-slate-700" />
          </div>
          <div>
            <div className="font-display text-lg font-bold">{stats?.total_courses ?? "—"} courses in Knowledge Base</div>
            <div className="text-xs text-slate-500">AI uses these to draft accurate replies.</div>
          </div>
        </div>
        <Link to="/knowledge" data-testid="dashboard-view-kb" className="text-xs font-semibold text-emerald-700 hover:underline">Manage courses →</Link>
      </div>
    </div>
  );
}
